import React, { useState } from 'react';
import { Drawer, Card, Typography, Tag, List, Button, Divider, message } from 'antd';
import { useQuery } from 'react-query';
import { debtService, Debt } from '../../services/debtService';
import DebtPaymentModalMobile from './DebtPaymentModalMobile';
import { CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Customer } from '../../services/customerService';

const { Text } = Typography;

const statusColor = {
  unpaid: 'red',
  partial: 'orange',
  paid: 'green',
};
const statusText = {
  unpaid: 'Chưa trả',
  partial: 'Còn nợ',
  paid: 'Đã trả',
};
const statusIcon = {
  unpaid: <ExclamationCircleOutlined />,
  partial: <ClockCircleOutlined />,
  paid: <CheckCircleOutlined />,
};

interface Props {
  debt: Debt;
  onClose: () => void;
  onPaid: () => void;
  customers?: Customer[];
  allDebts?: Debt[];
}

const DebtDetailModalMobile: React.FC<Props> = ({ debt, onClose, onPaid, customers = [], allDebts }) => {
  const { data, refetch } = useQuery(['debt-detail', debt.id], () => debtService.getDebtDetail(debt.id));
  const [payModal, setPayModal] = useState(false);

  // Dùng allDebts truyền vào nếu có, nếu không thì fetch
  const { data: fetchedDebts = [] } = useQuery(
    ['customer-debts', debt.customer_id],
    () => debtService.getDebts(String(debt.customer_id)),
    { enabled: !!debt.customer_id && !allDebts }
  );
  const debtsToShow = allDebts && allDebts.length > 0 ? allDebts : fetchedDebts;

  // Tổng hợp số liệu toàn bộ công nợ của khách hàng
  const stats = React.useMemo(() => {
    if (!debtsToShow || debtsToShow.length === 0) return { totalDebt: 0, totalPaid: 0, remain: 0, status: 'unpaid' as 'unpaid' };
    const totalDebt = debtsToShow.reduce((sum, d) => sum + d.debt_amount, 0);
    const totalPaid = debtsToShow.reduce((sum, d) => sum + d.paid_amount, 0);
    const remain = debtsToShow.reduce((sum, d) => sum + (d.debt_amount - d.paid_amount), 0);
    let status: 'unpaid' | 'partial' | 'paid' = 'paid';
    for (const d of debtsToShow) {
      if (d.status === 'unpaid') { status = 'unpaid'; break; }
      if (d.status === 'partial') { status = 'partial'; }
    }
    return { totalDebt, totalPaid, remain, status };
  }, [debtsToShow]);

  // Gom lịch sử giao dịch (ghi nợ + thanh toán) thành 1 bảng
  const transactions = React.useMemo(() => {
    if (!debtsToShow || debtsToShow.length === 0) return [];
    // Giao dịch ghi nợ
    const debtTxs = debtsToShow.map((d) => ({
      type: 'debt',
      date: d.created_at,
      amount: d.debt_amount,
      note: (d as any).note || '',
      id: d.id,
      voucher: (d as any).voucher_number || (d as any).stock_out_voucher_id || d.id,
    }));
    // Giao dịch thanh toán
    const paymentTxs = debtsToShow.flatMap((d) =>
      Array.isArray((d as any).payments)
        ? (d as any).payments.map((p: any) => ({
            type: 'payment',
            date: p.payment_date,
            amount: p.amount,
            note: p.note || '',
            id: p.id,
            voucher: (p as any).voucher_number || (d as any).voucher_number || (d as any).stock_out_voucher_id || d.id,
          }))
        : []
    );
    // Gộp và sort theo ngày
    const allTxs = [...debtTxs, ...paymentTxs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    // Tính số dư sau giao dịch
    let balance = 0;
    return allTxs.map((tx) => {
      if (tx.type === 'debt') {
        balance += tx.amount;
        return { ...tx, in: 0, out: tx.amount, balance: balance };
      } else {
        balance -= tx.amount;
        return { ...tx, in: tx.amount, out: 0, balance: balance };
      }
    });
  }, [debtsToShow]);

  if (!data) return null;
  
  // Sử dụng thông tin khách hàng từ API response nếu có, nếu không thì tìm trong customers
  const customerFromResponse = data.debt.customer_name ? {
    id: data.debt.customer_id,
    name: data.debt.customer_name,
    code: data.debt.customer_code,
    phone: data.debt.customer_phone,
    address: data.debt.customer_address
  } : null;
  const customer = customerFromResponse || customers.find(c => c.id === data.debt.customer_id);

  return (
    <Drawer
      open
      placement="bottom"
      height="90vh"
      onClose={onClose}
      bodyStyle={{ padding: 0, background: '#f5f6fa' }}
      destroyOnClose
      title={null}
    >
      <div style={{ padding: 8, minHeight: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Thông tin khách hàng + thống kê */}
        <Card size="small" style={{ borderRadius: 10, marginBottom: 8, padding: 0 }} bodyStyle={{ padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <UserOutlined style={{ color: '#1890ff', fontSize: 16 }} />
            <Text strong style={{ fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{customer ? customer.name : `Khách hàng ${data.debt.customer_id}`}</Text>
            <Tag color={statusColor[stats.status]} icon={statusIcon[stats.status]} style={{ marginLeft: 4, fontSize: 11, height: 22, lineHeight: '20px', padding: '0 6px' }}>{statusText[stats.status]}</Tag>
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Mã KH: {customer ? customer.code : data.debt.customer_id}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>Số nợ</div>
              <div style={{ color: '#faad14', fontWeight: 600, fontSize: 15 }}>{stats.totalDebt.toLocaleString('vi-VN')}</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>Đã trả</div>
              <div style={{ color: '#52c41a', fontWeight: 600, fontSize: 15 }}>{stats.totalPaid.toLocaleString('vi-VN')}</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>Còn lại</div>
              <div style={{ color: '#ff4d4f', fontWeight: 600, fontSize: 15 }}>{stats.remain.toLocaleString('vi-VN')}</div>
            </div>
          </div>
        </Card>
        {/* Lịch sử giao dịch */}
        <Card size="small" style={{ borderRadius: 10, flex: 1, minHeight: 120, marginBottom: 8, overflow: 'auto', padding: 0 }} bodyStyle={{ padding: 6 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, paddingLeft: 2 }}>Lịch sử giao dịch công nợ</div>
          {transactions.length > 0 ? (
            <List
              dataSource={transactions}
              renderItem={(tx, idx) => (
                <List.Item style={{ display: 'flex', alignItems: 'center', padding: '3px 0', fontSize: 12 }}>
                  <span style={{ flex: 0.5, textAlign: 'center' }}>{idx + 1}</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>{tx.date?.slice(0, 10)}</span>
                  <span style={{ flex: 1.5, textAlign: 'center', fontWeight: 500 }}>
                    {tx.type === 'debt'
                      ? `Ghi nợ${tx.voucher ? ' - ' + tx.voucher : ''}`
                      : `Thanh toán${tx.voucher ? ' - ' + tx.voucher : ''}`}
                  </span>
                  <span style={{ flex: 1, textAlign: 'center', color: '#52c41a', fontWeight: 500 }}>{tx.in > 0 ? `+${tx.in.toLocaleString('vi-VN')}` : ''}</span>
                  <span style={{ flex: 1, textAlign: 'center', color: '#ff4d4f', fontWeight: 500 }}>{tx.out > 0 ? `-${tx.out.toLocaleString('vi-VN')}` : ''}</span>
                  <span style={{ flex: 2, textAlign: 'center', color: '#888' }}>{tx.note}</span>
                  <span style={{ flex: 1, textAlign: 'center', fontWeight: 500 }}>{tx.balance.toLocaleString('vi-VN')}</span>
                </List.Item>
              )}
              header={
                <div style={{ position: 'sticky', top: 0, background: '#fafafa', zIndex: 2, display: 'flex', fontWeight: 700, color: '#888', padding: '4px 0', fontSize: 12 }}>
                  <span style={{ flex: 0.5, textAlign: 'center' }}>#</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>Ngày</span>
                  <span style={{ flex: 1.5, textAlign: 'center' }}>Loại</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>Vào</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>Ra</span>
                  <span style={{ flex: 2, textAlign: 'center' }}>Ghi chú</span>
                  <span style={{ flex: 1, textAlign: 'center' }}>Số dư</span>
                </div>
              }
            />
          ) : (
            <div style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>Không có giao dịch</div>
          )}
        </Card>
        {/* Nút thanh toán nổi */}
        <Button
          type="primary"
          block
          size="middle"
          style={{ position: 'sticky', bottom: 0, zIndex: 10, marginTop: 4, borderRadius: 8, fontWeight: 600, fontSize: 15, height: 38 }}
          onClick={() => setPayModal(true)}
          disabled={debtsToShow.filter(d => d.debt_amount > d.paid_amount).length === 0}
        >
          Thanh toán công nợ
        </Button>
        {payModal && (
          <DebtPaymentModalMobile
            debt={data.debt}
            debts={debtsToShow.filter(d => d.debt_amount > d.paid_amount)}
            onClose={() => setPayModal(false)}
            onPaid={() => {
              setPayModal(false);
              refetch();
              onPaid();
              message.success('Thanh toán thành công!');
            }}
          />
        )}

        <Divider style={{ margin: '10px 0' }} />
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Ghi chú</div>
        <div style={{ minHeight: 36, color: '#666', background: '#fff', borderRadius: 4, padding: 8, border: '1px solid #f0f0f0', fontSize: 12 }}>
          {'Không có ghi chú'}
        </div>
      </div>
    </Drawer>
  );
};

export default DebtDetailModalMobile;
